/**
 * OperationsPulsePanel — "LIVE ARCHIVE" status strip (June 2026 redesign).
 *
 * Real database-derived totals only, from cached /api/home/live-archive:
 *   Dossiers · Verified Sources · Documented Links · Published Media
 * plus a genuine last-update timestamp. Numbers count up on load
 * (disabled under prefers-reduced-motion).
 */
import React, { useEffect, useState } from "react";
import { Layers, Radar, GitBranch, Image as ImageIcon } from "lucide-react";
import axios from "axios";
import { relTime, useCountUp } from "./homeShared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Cell = ({ icon: Icon, label, value, testid }) => {
  const n = useCountUp(value);
  return (
    <div
      data-testid={testid}
      className="min-w-0 flex flex-col gap-1 md:gap-1.5 px-2 md:px-5 py-2.5 md:py-5 border-l first:border-l-0"
      style={{ borderColor: "#2D2A26" }}
    >
      <div className="flex items-center gap-1.5 md:gap-2 font-mono text-[7px] md:text-[9px] uppercase tracking-[0.15em] md:tracking-[0.3em] text-[#8A8377] min-w-0">
        <Icon size={10} className="hidden md:block shrink-0 text-[#D92D20]" />
        <span className="leading-snug">{label}</span>
      </div>
      <div className="font-display text-[17px] md:text-[30px] lg:text-[34px] leading-tight text-[#E4DFD3]">
        {typeof n === "number" ? n.toLocaleString() : "—"}
      </div>
    </div>
  );
};

const OperationsPulsePanel = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/home/live-archive`, { timeout: 10000 });
        if (!cancelled) setData(data);
      } catch { /* section degrades silently */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section
      data-testid="home-live-archive"
      className="relative border-y"
      style={{ borderColor: "#2D2A26", background: "#0B0A0D" }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="pt-3 pb-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="relative flex w-2 h-2 shrink-0">
              <span className="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D92D20] opacity-60" />
              <span className="relative inline-flex rounded-full w-2 h-2 bg-[#D92D20]" />
            </span>
            <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#A39E93] truncate">
              Live Archive
            </span>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {data?.last_update && (
              <span
                data-testid="live-archive-updated"
                className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#7B7168]"
              >
                Updated {relTime(data.last_update)}
              </span>
            )}
            <span className="hidden sm:inline font-mono text-[9px] uppercase tracking-[0.4em] text-[#3FB950]">
              LIVE
            </span>
          </div>
        </div>
        <div
          className="grid grid-cols-4 border"
          style={{ borderColor: "#2D2A26", background: "#0F0E12" }}
        >
          <Cell testid="pulse-dossiers" icon={Layers} label="Dossiers Archived" value={data?.dossiers} />
          <Cell testid="pulse-sources" icon={Radar} label="Verified Sources" value={data?.verified_sources} />
          <Cell testid="pulse-links" icon={GitBranch} label="Documented Links" value={data?.documented_links} />
          <Cell testid="pulse-media" icon={ImageIcon} label="Published Media" value={data?.published_media} />
        </div>
      </div>
    </section>
  );
};

export default OperationsPulsePanel;
